import { Dispatch, SetStateAction, useContext, useEffect, useState, memo } from 'react';
import { Card, Col, Row } from 'react-bootstrap';

import { usePrivileges } from '../../../Auth/usePrivileges';
import { LotDetailContext } from '../../../Contexts/LotDetailContext';
import BlockLotButton from '../LotSummaryButtons/BlockLotButton';
import CancelSplitLotButton from '../LotSummaryButtons/CancelSplitLotButton';
import ConfirmReviewButton from '../LotSummaryButtons/ConfirmReviewButton';
import DeleteLotButton from '../LotSummaryButtons/DeleteLotButton';
import ExportCollectionUpdateButton from '../LotSummaryButtons/ExportCollectionUpdateButton';
import ExportProcessDataButton from '../LotSummaryButtons/ExportProcessDataButton';
import GradeLotButton from '../LotSummaryButtons/GradeLotButton';
import LockLotButton from '../LotSummaryButtons/LockLotButton';
import LotNumberComponent from '../LotSummaryButtons/LotNumberComponent';
import ProcessAgainButton from '../LotSummaryButtons/ProcessAgainButton';
import ResendReleaseSampleButton from '../LotSummaryButtons/ResendReleaseSampleButton';
import SplitLotButton from '../LotSummaryButtons/SplitLotButton';
import UndoGradedButton from '../LotSummaryButtons/UndoGradedButton';
import UnlockLotButton from '../LotSummaryButtons/UnlockLotButton';
import ViewReportButton from '../LotSummaryButtons/ViewReportButton';
import LotRevisionComponent from './LotRevisionComponent';
import { ExtruderField } from './SummaryFields/ExtruderField';
import { GradeFields } from './SummaryFields/GradeFields';
import { LineField } from './SummaryFields/LineField';
import { HeaderTextField } from './SummaryFields/LotDataFields/HeaderTextField';
import { PackageCountFields } from './SummaryFields/LotDataFields/PackageCountField';
import { RFTCodeField } from './SummaryFields/LotDataFields/RFTCodeField';
import { LotProcessingStateFields } from './SummaryFields/LotProcessingStateFields';
import { LotTimingFields } from './SummaryFields/LotTimingFields';
import { LotWeightFields } from './SummaryFields/LotWeightFields';
import { MaterialCodeFields } from './SummaryFields/MaterialCodeFields';

interface LotDetailsSummaryProps {
    setLoading: Dispatch<SetStateAction<boolean>>;
}

const LotDetailsSummary = memo(function LotDetailsSummary({ setLoading }: LotDetailsSummaryProps) {
    const {
        lotDetail: {
            summary
        },
        lotFlags: {
            hasRFTCode,
            hasHeaderText,
            hasPackoutGrade,
            hasExtruder,
            hasLotSize,
            hasPackageCount,
            hasMaterialCodes
        }
    } = useContext(LotDetailContext);

    const [lotNumber, setLotNumber] = useState(summary.lotNumber);
    useEffect(() => {
        setLotNumber(summary.lotNumber);
    }, [summary.lotNumber]);

    const { canProcess, canReview, canGrade, supervisor, admin } = usePrivileges(summary.businessUnitCode);

    const allPrivilegesLoaded = [
        canProcess,
        canReview,
        canGrade,
        supervisor,
        admin
    ].every(x => x !== undefined);

    return (
        <Card bg='light' className='rounded-3 mb-3'>
            <Card.Title className="px-3 pt-3">
                <Row>
                    <Col sm={12} md={3}>
                        <LotNumberComponent lotNumber={lotNumber} lotStatus={summary.currentStatus} isLocked={summary.isLocked} />
                    </Col>
                    {allPrivilegesLoaded &&
                        <Col className="d-flex flex-wrap gap-1 justify-content-md-end">
                            <ProcessAgainButton setLoading={setLoading} />
                            <ConfirmReviewButton />
                            <GradeLotButton />
                            <UndoGradedButton setLoading={setLoading} />
                            <ResendReleaseSampleButton setLoading={setLoading} />
                            <SplitLotButton />
                            <CancelSplitLotButton />
                            <ViewReportButton />
                            <ExportCollectionUpdateButton />
                            <ExportProcessDataButton />
                            <BlockLotButton setLoading={setLoading} />
                            <LockLotButton setLoading={setLoading} />
                            <UnlockLotButton setLoading={setLoading} />
                            <DeleteLotButton setLoading={setLoading} />
                        </Col>
                    }
                </Row>
                <Row>
                    <LotRevisionComponent setLoading={setLoading} />
                </Row>
                <hr />
            </Card.Title>
            <Card.Body className="pt-0">
                <Row className="gy-1 mb-1">
                    <GradeFields hasPackoutGrade={hasPackoutGrade} />
                </Row>
                <Row className="gy-1 mb-3">
                    {hasMaterialCodes &&
                        <MaterialCodeFields />
                    }
                </Row>
                <Row className="gy-1 mb-1">
                    <LineField />
                    {hasExtruder &&
                        <ExtruderField />
                    }
                </Row>
                <Row className="gy-1 mb-1">
                    <LotWeightFields hasLotSize={hasLotSize} />
                    {hasPackageCount &&
                        <PackageCountFields />
                    }
                </Row>
                <Row className="gy-1 mb-1">
                    {hasRFTCode &&
                        <RFTCodeField />
                    }
                    {hasHeaderText &&
                        <HeaderTextField />
                    }
                </Row>
                <Row className="gy-1 mb-1">
                    <LotTimingFields />
                </Row>
                <Row className="gy-1 mt-3">
                    <LotProcessingStateFields />
                </Row>
            </Card.Body>
        </Card>
    );
});

export default LotDetailsSummary;